<template>
  <form @submit.prevent="handleSubmit" class="p-4 space-y-4">
    <!-- Order Search -->
    <div class="space-y-2">
      <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">Order Reference</label>
      <div class="relative">
        <input
          type="text"
          v-model="orderSearch"
          @input="handleOrderSearch"
          placeholder="Search order by number..."
          :disabled="!!props.payment"
          class="mt-2 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-dark-600 bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100 placeholder-gray-500 dark:placeholder-dark-400"
        >
        <div v-if="showOrderResults && !props.payment" class="absolute z-10 w-full mt-1 bg-white dark:bg-dark-800 shadow-lg rounded-md border border-gray-300 dark:border-dark-600">
          <div v-if="loading" class="p-2 text-center text-gray-500 dark:text-dark-400">Loading...</div>
          <div v-else-if="filteredOrders.length === 0" class="p-2 text-center text-gray-500 dark:text-dark-400">No orders found</div>
          <div v-else class="max-h-60 overflow-y-auto">
            <div
              v-for="order in filteredOrders"
              :key="order.id"
              @click="selectOrder(order)"
              class="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 cursor-pointer"
            >
              <div class="font-medium text-gray-900 dark:text-dark-100">Order #{{ order.order_number }}</div>
              <div class="text-sm text-gray-500 dark:text-dark-400">
                Client: {{ order.client_name }}
                <br>
                Total Amount: ₱{{ formatCurrency(order.total_amount) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Selected Order Details -->
    <div v-if="selectedOrder" class="bg-gray-50 dark:bg-dark-700 p-3 rounded border border-gray-200 dark:border-dark-600">
      <div class="flex justify-between items-center mb-2">
        <h4 class="text-sm font-medium text-gray-900 dark:text-dark-100">{{ selectedOrder.order_number }}</h4>
        <span 
          class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
          :class="{
            'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300': selectedOrder.status === 'completed',
            'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300': selectedOrder.status === 'for_collection',
            'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300': selectedOrder.status === 'production',
            'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300': selectedOrder.status === 'in_review',
            'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300': selectedOrder.status === 'ready_release',
            'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300': selectedOrder.status === 'cancelled',
            'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-300': !['completed', 'for_collection', 'production', 'in_review', 'ready_release', 'cancelled'].includes(selectedOrder.status)
          }"
        >
          {{ selectedOrder.status ? selectedOrder.status.replace(/_/g, ' ').toUpperCase() : 'UNKNOWN' }}
        </span>
      </div>
      <div class="grid grid-cols-3 gap-3 text-xs">
        <div>
          <p class="text-gray-500 dark:text-dark-400">Client:</p>
          <p class="font-medium text-gray-900 dark:text-dark-100">{{ selectedOrder.client_name }}</p>
        </div>
        <div>
          <p class="text-gray-500 dark:text-dark-400">Total:</p>
          <p class="font-medium text-gray-900 dark:text-dark-100">₱{{ formatCurrency(selectedOrder.total_amount) }}</p>
        </div>
        <div>
          <p class="text-gray-500 dark:text-dark-400">Balance:</p>
          <p class="font-medium text-green-600 dark:text-green-400">₱{{ formatCurrency(selectedOrder.remaining_balance || selectedOrder.total_amount) }}</p>
        </div>
      </div>
    </div>

    <!-- Payment Method & Type Row -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div class="space-y-2">
      <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">Payment Method</label>
      <select
        v-model="formData.payment_method"
        class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
        required
      >
        <option value="">Select Payment Method</option>
        <option value="cash">Cash</option>
        <option value="bank_transfer">Bank Transfer</option>
        <option value="check">Check</option>
        <option value="credit">Credit (Terminal)</option>
      </select>
    </div>

    <div class="space-y-2">
      <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">Payment Type</label>
      <select
        v-model="formData.payment_type"
        :disabled="isPaymentTypeAutoSelected"
        class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100 dark:disabled:bg-dark-600 disabled:text-gray-600 dark:disabled:text-dark-400 bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
        required
      >
        <option value="">Select Payment Type</option>
        <option v-for="type in paymentTypes" :key="type.id" :value="type.name">
          {{ type.name }}
        </option>
      </select>
        <div v-if="paymentTypeHelperText" class="mt-1 text-xs text-blue-600 dark:text-blue-400">
        <i class="fas fa-info-circle mr-1"></i>
          {{ paymentTypeHelperText }}
      </div>
      </div>
    </div>

    <!-- Common Fields -->
    <div v-if="formData.payment_method" class="space-y-4">
      <!-- Amount & Date Row -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <!-- Amount field for all except Check -->
      <div v-if="formData.payment_method !== 'check'">
        <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">
          Amount
          <span v-if="isProcessingOCR && formData.payment_method === 'bank_transfer'" class="text-blue-600 dark:text-blue-400 text-xs ml-1">
            <i class="fas fa-spinner fa-spin"></i>
            Auto-filling...
          </span>
        </label>
        <div class="relative">
          <input
            v-model.number="formData.amount"
            type="number"
            step="0.01"
            class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
            :class="{ 'pr-10': isProcessingOCR && formData.payment_method === 'bank_transfer' }"
            :disabled="isProcessingOCR && formData.payment_method === 'bank_transfer'"
            required
          />
          <div v-if="isProcessingOCR && formData.payment_method === 'bank_transfer'" class="absolute inset-y-0 right-0 flex items-center pr-3 mt-1">
            <i class="fas fa-spinner fa-spin text-blue-500"></i>
          </div>
        </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">Date</label>
          <input
            type="date"
            v-model="formData.date"
            required
            class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
          >
        </div>
      </div>

      <!-- Bank Transfer Fields -->
      <div v-if="formData.payment_method === 'bank_transfer'" class="space-y-3">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">
            Bank Name
            <span v-if="isProcessingOCR" class="text-blue-600 dark:text-blue-400 text-xs ml-1">
              <i class="fas fa-spinner fa-spin"></i>
              Auto-filling...
            </span>
          </label>
          <input
            v-model="formData.bank_name"
            type="text"
            class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
            required
          />
        </div>
        <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">
              Transfer Date
              <span v-if="isProcessingOCR" class="text-blue-600 dark:text-blue-400 text-xs ml-1">
                <i class="fas fa-spinner fa-spin"></i>
                Auto-filling...
              </span>
            </label>
            <div class="relative">
              <input
                v-model="formData.transfer_date"
                type="date"
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
                :class="{ 'pr-10': isProcessingOCR }"
                :disabled="isProcessingOCR"
                required
              />
              <div v-if="isProcessingOCR" class="absolute inset-y-0 right-0 flex items-center pr-3 mt-1">
                <i class="fas fa-spinner fa-spin text-blue-500"></i>
              </div>
            </div>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">
            Transaction Reference
            <span v-if="isProcessingOCR" class="text-blue-600 dark:text-blue-400 text-xs ml-1">
              <i class="fas fa-spinner fa-spin"></i>
              Extracting data from image...
            </span>
          </label>
          <div class="relative">
          <input
              v-model="formData.transaction_reference"
              type="text"
            class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
              :class="{ 'pr-10': isProcessingOCR }"
            required
              :disabled="isProcessingOCR"
            />
            <div v-if="isProcessingOCR" class="absolute inset-y-0 right-0 flex items-center pr-3 mt-1">
              <i class="fas fa-spinner fa-spin text-blue-500"></i>
            </div>
          </div>
          <p v-if="formData.payment_method === 'bank_transfer' && !formData.transaction_reference" class="mt-1 text-xs text-gray-500 dark:text-dark-400">
            <i class="fas fa-magic mr-1"></i>
            Upload payment proof image to auto-extract reference, amount & date
          </p>
        </div>
      </div>

      <!-- Check Fields -->
      <div v-if="formData.payment_method === 'check'" class="space-y-3">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">Cheque Amount</label>
          <input
            v-model.number="formData.amount"
            type="number"
            step="0.01"
            class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
            required
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">Bank Name</label>
          <input
            v-model="formData.bank_name"
            type="text"
            class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
            required
          />
        </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">Cheque Number</label>
          <input
            v-model="formData.cheque_number"
            type="text"
            class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
            required
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">Cheque Date</label>
          <input
            v-model="formData.cheque_date"
            type="date"
            class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
            required
          />
          </div>
        </div>
      </div>

      <!-- Credit Fields -->
      <div v-if="formData.payment_method === 'credit'" class="space-y-3">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">Transaction Reference</label>
          <input
            v-model="formData.transaction_reference"
            type="text"
            class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
            required
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">Card Type</label>
          <select
            v-model="formData.card_type"
            class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100"
            required
          >
            <option value="">Select Card Type</option>
            <option value="visa">Visa</option>
            <option value="mastercard">Mastercard</option>
            <option value="amex">American Express</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>
    </div>
    </div>

    <!-- Notes -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">Notes</label>
      <textarea
        v-model="formData.notes"
        rows="2"
        class="mt-1 block w-full rounded-md border-gray-300 dark:border-dark-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-white dark:bg-dark-700 text-gray-900 dark:text-dark-100 placeholder-gray-500 dark:placeholder-dark-400"
      ></textarea>
    </div>

    <!-- Payment Image Upload -->
    <div v-if="formData.payment_method !== 'cash'">
      <label class="block text-sm font-medium text-gray-700 dark:text-dark-300">
        Payment Image
        <span v-if="formData.payment_method === 'bank_transfer'" class="text-red-500 ml-1">*</span>
      </label>
      <div class="flex items-center space-x-4">
        <!-- Image Upload Area -->
        <div 
          class="flex-1 border-2 border-dashed rounded-lg p-3 text-center transition-colors cursor-pointer hover:border-blue-400 dark:hover:border-blue-400"
          :class="[
            imagePreview || formData.image_url ? 
              'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20' : 
              formData.payment_method === 'bank_transfer' && !imagePreview && !formData.image_url ? 
                'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20' : 
                isDragging ? 'border-blue-400 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-dark-600'
          ]"
          @click="$refs.imageInput.click()"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleImageDrop"
          @paste.prevent="handleImagePaste"
          tabindex="0"
          @keydown.prevent
          ref="pasteArea"
        >
          <input
            ref="imageInput"
            type="file"
            class="hidden"
            accept="image/jpeg,image/png,image/gif"
            @change="handleImageUpload"
          />
          <div class="space-y-2">
            <i class="fas fa-image text-xl text-gray-400 dark:text-dark-400"></i>
            <div class="text-sm text-gray-600 dark:text-dark-400 space-y-1">
              <p class="font-medium text-xs">Payment proof upload:</p>
              <div class="flex items-center justify-center gap-3 text-xs">
                <span class="flex items-center gap-1">
                  <i class="fas fa-mouse-pointer text-blue-500"></i>
                  Browse
                </span>
                <span class="flex items-center gap-1 text-green-600 dark:text-green-400 font-semibold">
                  <i class="fas fa-paste text-green-500"></i>
                  Ctrl+V
                </span>
                <span class="flex items-center gap-1">
                  <i class="fas fa-hand-paper text-purple-500"></i>
                  Drag
                </span>
            </div>
              <div class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-600 rounded p-1">
                <p class="text-green-700 dark:text-green-300 text-xs text-center">
                  <i class="fas fa-lightbulb mr-1"></i>
                  Copy image & press Ctrl+V
                </p>
              </div>
              <div v-if="formData.payment_method === 'bank_transfer'" class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-600 rounded p-1">
                <p class="text-blue-700 dark:text-blue-300 text-xs text-center">
                  <i class="fas fa-magic mr-1"></i>
                  Auto-extracts reference, amount & date
                </p>
              </div>
              <p class="text-xs text-gray-500 dark:text-dark-400 text-center">PNG, JPG, GIF (10MB max)</p>
            </div>
          </div>
        </div>

        <!-- Preview Image -->
        <div v-if="imagePreview || formData.image_url" class="relative w-24 h-24">
          <img 
            :src="imagePreview || formData.image_url" 
            class="w-24 h-24 object-cover rounded-lg"
            alt="Payment image preview"
          />
          <button
            @click.stop="removeImage"
            type="button"
            class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      <div v-if="validationErrors.image_url" class="mt-1 text-sm text-red-600 dark:text-red-400">
        {{ validationErrors.image_url }}
      </div>
      <div v-else-if="formData.payment_method === 'bank_transfer' && !imagePreview && !formData.image_url" class="mt-1 text-sm text-amber-600 dark:text-amber-400">
        <i class="fas fa-exclamation-triangle mr-1"></i>
        Payment proof image is required for bank transfers
      </div>
    </div>

    <!-- OCR Debug Section (only shown in development) -->
    <div v-if="ocrExtractedText && isDevelopment" class="bg-gray-50 dark:bg-dark-700 p-3 rounded border border-gray-200 dark:border-dark-600">
      <details>
        <summary class="text-sm font-medium text-gray-700 dark:text-dark-300 cursor-pointer">
          <i class="fas fa-eye mr-1"></i>
          OCR Extracted Text (Debug)
        </summary>
        <div class="mt-2 text-xs text-gray-600 dark:text-dark-400 whitespace-pre-wrap font-mono bg-white dark:bg-dark-800 p-2 rounded border border-gray-200 dark:border-dark-600">
          {{ ocrExtractedText }}
        </div>
      </details>
    </div>

    <!-- Form Actions -->
    <div class="flex justify-end space-x-3 pt-4">
      <button
        type="button"
        @click="$emit('cancel')"
        :disabled="isSubmitting"
        class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Cancel
      </button>
      <button
        type="submit"
        :disabled="isSubmitting || submitDisabled"
        class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <template v-if="isSubmitting">
          <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Processing...
        </template>
        <template v-else>
          {{ props.payment ? 'Update Payment' : 'Save Payment' }}
        </template>
      </button>
    </div>
  </form>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useApi } from '@/composables/useApi';
import { useToast } from '@/composables/useToast';

const props = defineProps({
  payment: {
    type: Object,
    default: null
  },
  initialOrder: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['submit', 'cancel']);
const api = useApi();
const toast = useToast();

const selectedOrder = ref(null);
const formData = ref({
  date: new Date().toISOString().split('T')[0],
  order_id: null,
  amount: '',
  payment_method: 'cash',
  payment_type: '',
  transaction_reference: '',
  notes: '',
  status: 'pending',
  bank_name: '',
  transfer_date: '',
  cheque_number: '',
  cheque_date: '',
  card_type: '',
  image_url: null,
  imageFile: null
});

const loading = ref(false);
const isSubmitting = ref(false);
const orderSearch = ref('');
const showOrderResults = ref(false);
const filteredOrders = ref([]);
const paymentTypes = ref([]);
let searchTimeout = null;

// Add validationErrors ref
const validationErrors = ref({
  order_id: '',
  amount: '',
  payment_method: '',
  payment_type: '',
  date: '',
  bank_name: '',
  transfer_date: '',
  cheque_number: '',
  cheque_date: '',
  image_url: '',
  card_type: ''
});

// Add these refs for image handling
const imageInput = ref(null);
const imagePreview = ref(null);

// Add this at the top of the script with other refs
const submitTimeout = ref(null);

// Add submitDisabled ref for cooldown
const submitDisabled = ref(false);
const isDragging = ref(false);
const isProcessingOCR = ref(false);
const ocrExtractedText = ref('');

// Development mode check
const isDevelopment = computed(() => {
  return import.meta.env.DEV || false;
});

// Payment type auto-selection logic
const isPaymentTypeAutoSelected = computed(() => {
  if (!selectedOrder.value || !formData.value.amount || formData.value.amount <= 0) {
    return false;
  }
  
  // Always disable for collection orders
  if (selectedOrder.value.for_collection) {
    return true;
  }
  
  // Check if current payment type matches auto-selection criteria
  const totalAmount = Number(selectedOrder.value.total_amount) || 0;
  const totalPaid = Number(selectedOrder.value.total_paid) || 0;
  const remainingBalance = totalAmount - totalPaid;
  const paymentAmount = Number(formData.value.amount);
  
  // Disable if amount triggers auto-selection
  if (paymentAmount >= remainingBalance && remainingBalance > 0) {
    return true; // Fully Paid scenario
  }
  if (totalPaid === 0 && paymentAmount < totalAmount) {
    return true; // Downpayment scenario
  }
  if (totalPaid > 0 && paymentAmount < remainingBalance) {
    return true; // Balance scenario
  }
  
  return false;
});

const paymentTypeHelperText = computed(() => {
  if (!selectedOrder.value || !formData.value.amount || formData.value.amount <= 0) {
    return '';
  }
  
  // Collection order helper text
  if (selectedOrder.value.for_collection) {
    return 'Auto-set to "Collection" - order is flagged for collection';
  }
  
  const totalAmount = Number(selectedOrder.value.total_amount) || 0;
  const totalPaid = Number(selectedOrder.value.total_paid) || 0;
  const remainingBalance = totalAmount - totalPaid;
  const paymentAmount = Number(formData.value.amount);
  
  // Auto-selection helper texts
  if (paymentAmount >= remainingBalance && remainingBalance > 0) {
    return `Auto-set to "Fully Paid" - amount (₱${paymentAmount.toLocaleString()}) covers remaining balance (₱${remainingBalance.toLocaleString()})`;
  }
  if (totalPaid === 0 && paymentAmount < totalAmount) {
    return `Auto-set to "Downpayment" - first payment (₱${paymentAmount.toLocaleString()}) of total (₱${totalAmount.toLocaleString()})`;
  }
  if (totalPaid > 0 && paymentAmount < remainingBalance) {
    return `Auto-set to "Balance" - partial payment (₱${paymentAmount.toLocaleString()}) towards remaining (₱${remainingBalance.toLocaleString()})`;
  }
  
  return '';
});

const searchOrders = async () => {
  try {
    if (!orderSearch.value || orderSearch.value.length < 2) {
      filteredOrders.value = [];
      showOrderResults.value = false;
      return;
    }

    loading.value = true;
    const searchTerm = orderSearch.value.toUpperCase();
    
    console.log('Searching for orders with term:', searchTerm);
    
    const response = await api.get('/orders', { 
      params: {
        search: searchTerm,
        status: { not: ['completed', 'cancelled'] },
        limit: 10
      }
    });

    console.log('Search response:', response?.data);

    if (response?.data?.data) {
      const ordersWithPayments = await Promise.all(
        response.data.data.map(async (order) => {
          console.log('Processing order:', order.order_number);
          
          // Fetch complete order details with items to get accurate total
          const orderDetailsResponse = await api.get(`/orders/${order.id}`);
          const orderDetails = orderDetailsResponse?.data || order;
          
          console.log('Order details response for', order.order_number, ':', orderDetails);
          
          // Fetch payments
          const paymentsResponse = await api.get(`/orders/${order.id}/payments`);
          const payments = paymentsResponse?.data || [];
          
          console.log('Payments for order', order.order_number, ':', payments);
          console.log('Payment statuses:', payments.map(p => ({ id: p.id, amount: p.amount, status: p.status })));
          
          // Calculate total paid from approved and completed payments
          const totalPaid = payments
            .filter(p => p.status === 'completed' || p.status === 'approved')
            .reduce((sum, p) => sum + Number(p.amount || 0), 0);
          
          console.log('Total paid amount calculated for', order.order_number, ':', totalPaid);
          
          // Use the total_amount from order details which includes all items and discounts
          const totalAmount = Number(orderDetails.total_amount) || 0;
          const remainingBalance = totalAmount - totalPaid;
          
          console.log('Remaining balance calculated for', order.order_number, ':', remainingBalance);
          
          const result = {
            ...order,
            ...orderDetails,
            total_amount: totalAmount,
            total_paid: totalPaid,
            remaining_balance: remainingBalance,
            payments: payments
          };
          
          console.log('Final order object for', order.order_number, ':', {
            total_amount: result.total_amount,
            total_paid: result.total_paid,
            remaining_balance: result.remaining_balance
          });
          
          return result;
        })
      );

      // Only show orders with a remaining balance
      filteredOrders.value = ordersWithPayments.filter(order => 
        order.total_amount > 0 && order.remaining_balance > 0
      );
      
      console.log('Filtered orders with complete details:', filteredOrders.value.map(o => ({
        order_number: o.order_number,
        total_amount: o.total_amount,
        total_paid: o.total_paid,
        remaining_balance: o.remaining_balance
      })));
    } else {
      filteredOrders.value = [];
    }
    showOrderResults.value = true;
  } catch (error) {
    console.error('Error searching orders:', error);
    filteredOrders.value = [];
  } finally {
    loading.value = false;
  }
};

const handleOrderSearch = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(searchOrders, 300);
};

const selectOrder = (order) => {
  // Ensure we have the payments array
  const payments = order.payments || [];
  
  // Calculate total paid from approved and completed payments
  const totalPaid = payments
    .filter(p => p.status === 'completed' || p.status === 'approved')
    .reduce((sum, p) => sum + Number(p.amount || 0), 0);
  
  // Calculate remaining balance
  const totalAmount = Number(order.total_amount) || 0;
  const remainingBalance = totalAmount - totalPaid;
  
  // Update the selected order with calculated values
  selectedOrder.value = {
    ...order,
    total_paid: totalPaid,
    remaining_balance: remainingBalance
  };
  
  formData.value.order_id = order.id;
  orderSearch.value = order.order_number;
  showOrderResults.value = false;
  
  // Initialize amount to 0 instead of remaining balance
  formData.value.amount = 0;
  
  // Auto-select payment type based on for_collection flag
  if (order.for_collection) {
    formData.value.payment_type = 'Collection';
    console.log('[PaymentForm] Auto-selected payment type "Collection" for order flagged for collection');
  } else {
    // Reset payment type for other orders to let user choose
    formData.value.payment_type = '';
  }
  
  console.log('Selected order details:', {
    id: order.id,
    order_number: order.order_number,
    status: order.status,
      for_collection: order.for_collection,
    total_amount: totalAmount,
    total_paid: totalPaid,
    remaining_balance: remainingBalance,
    payments: payments,
      auto_selected_payment_type: order.for_collection ? 'Collection' : 'None'
  });
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

const validateForm = () => {
  if (!selectedOrder.value?.id) {
    toast.error('Please select an order first');
    return false;
  }

  if (!formData.value.amount || isNaN(formData.value.amount) || Number(formData.value.amount) <= 0) {
    toast.error('Please enter a valid amount');
    return false;
  }

  // Check if amount exceeds remaining balance
  const remainingBalance = Number(selectedOrder.value.remaining_balance) || 0;
  if (Number(formData.value.amount) > remainingBalance) {
    toast.warning(`Amount exceeds remaining balance (₱${formatCurrency(remainingBalance)}). Proceeding anyway.`);
    // We're allowing this but showing a warning, as sometimes users need to record overpayments
  }

  if (!formData.value.payment_method) {
    toast.error('Please select a payment method');
    return false;
  }

  if (!formData.value.payment_type) {
    toast.error('Please select a payment type');
    return false;
  }

  if (!formData.value.date) {
    toast.error('Please select a payment date');
    return false;
  }

  if (formData.value.payment_method === 'bank_transfer') {
    if (!formData.value.bank_name) {
      toast.error('Bank name is required for bank transfers');
      return false;
    }
    if (!formData.value.transfer_date) {
      toast.error('Transfer date is required for bank transfers');
      return false;
    }
    if (!formData.value.imageFile && !formData.value.image_url) {
      toast.error('Payment proof image is required for bank transfers');
      validationErrors.value.image_url = 'Payment proof image is required';
      return false;
    }
  }

  if (formData.value.payment_method === 'check') {
    if (!formData.value.bank_name) {
      toast.error('Bank name is required for check payments');
      return false;
    }
    if (!formData.value.cheque_date) {
      toast.error('Cheque date is required for check payments');
      return false;
    }
    if (!formData.value.cheque_number) {
      toast.error('Cheque number is required for check payments');
      return false;
    }
  }

  return true;
};

const handleImageDrop = (event) => {
  isDragging.value = false;
  
  try {
    const files = event.dataTransfer.files;
    
    if (files.length === 0) {
      toast.error('No files were dropped. Please try again.');
      return;
    }
    
    if (files.length > 1) {
      toast.error('Please drop only one image file at a time.');
      return;
    }
    
    const file = files[0];
    console.log('Payment image dropped:', file.name, file.type, file.size);
    validateAndProcessImage(file);
    toast.success('Image dropped successfully!');
  } catch (error) {
    console.error('Error handling drop event:', error);
    toast.error('Failed to process dropped image. Please try again.');
  }
};

const handleImageUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    validateAndProcessImage(file);
  }
};

// Handle paste event for clipboard images
const handleImagePaste = async (event) => {
  console.log('Paste event triggered:', event);
  event.preventDefault();
  event.stopPropagation();
  
  try {
    let imageFound = false;
    
    // First, try to get image from clipboardData (works for most cases)
    if (event.clipboardData && event.clipboardData.items) {
      console.log('Checking clipboardData items:', event.clipboardData.items.length);
      
      for (let i = 0; i < event.clipboardData.items.length; i++) {
        const item = event.clipboardData.items[i];
        console.log('Clipboard item:', item.kind, item.type);
        
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            console.log('Payment image pasted from clipboardData:', file.name || 'clipboard-image', file.type, file.size);
            validateAndProcessImage(file);
            toast.success('Payment image pasted successfully!');
            imageFound = true;
            return;
          }
        }
      }
    }
    
    // Second, try to get image from files (for drag & drop compatibility)
    if (event.clipboardData && event.clipboardData.files && event.clipboardData.files.length > 0) {
      console.log('Checking clipboardData files:', event.clipboardData.files.length);
      
      for (let i = 0; i < event.clipboardData.files.length; i++) {
        const file = event.clipboardData.files[i];
        console.log('Clipboard file:', file.name, file.type, file.size);
        
        if (file.type.startsWith('image/')) {
          console.log('Payment image found in files');
          validateAndProcessImage(file);
          toast.success('Payment image pasted successfully!');
          imageFound = true;
          return;
        }
      }
    }
    
    // Third, try modern Clipboard API (for additional browser support)
    if (!imageFound && navigator.clipboard && navigator.clipboard.read) {
      try {
        console.log('Trying modern Clipboard API...');
        const clipboardContents = await navigator.clipboard.read();
        console.log('Clipboard contents:', clipboardContents);
        
        for (const item of clipboardContents) {
          console.log('Clipboard item types:', item.types);
          for (const type of item.types) {
            if (type.startsWith('image/')) {
              const blob = await item.getType(type);
              const file = new File([blob], 'pasted-payment-image', { type: blob.type });
              console.log('Payment image from Clipboard API:', file.type, file.size);
              validateAndProcessImage(file);
              toast.success('Payment image pasted successfully!');
              imageFound = true;
              return;
            }
          }
        }
      } catch (clipboardError) {
        console.warn('Clipboard API failed:', clipboardError);
      }
    }
    
    if (!imageFound) {
      console.log('No image found in clipboard');
      toast.error('No image found in clipboard. Try copying an image first (Ctrl+C), then paste it here (Ctrl+V).');
    }
  } catch (error) {
    console.error('Error handling paste event:', error);
    toast.error('Failed to paste image. Please try copying the image again.');
  }
};

// Function to extract bank name from OCR text
const extractBankName = (text) => {
  // List of e-wallets and banks (e-wallets prioritized)
  const ewallets = [
    'GCash', 'Maya', 'PayMaya', 'GrabPay', 'Coins.ph'
  ];
  const banks = [
    'BPI', 'BDO', 'UnionBank', 'Metrobank', 'Landbank', 'PNB', 'Chinabank', 'EastWest',
    'Security Bank', 'RCBC', 'UCPB', 'PSBank', 'DBP', 'AUB', 'PBCom', 'Robinsons Bank', 'ING', 'GoTyme',
    'CIMB', 'Tonik', 'SeaBank', 'HSBC', 'Standard Chartered', 'Bank of Commerce'
  ];
  const cleanText = text.replace(/\s+/g, ' ').toLowerCase();
  // 1. Check for 'Sent via' or 'Transfer via' (case-insensitive)
  const viaMatch = text.match(/(?:sent|transfer) via ([a-zA-Z0-9 .]+)/i);
  if (viaMatch && viaMatch[1]) {
    // Try to match e-wallets first
    for (const ew of ewallets) {
      const regex = new RegExp(`\\b${ew}\\b`, 'i');
      if (regex.test(viaMatch[1])) {
        console.log('Extracted e-wallet from via:', ew);
        return ew;
      }
    }
    // Then try banks
    for (const bank of banks) {
      const regex = new RegExp(`\\b${bank}\\b`, 'i');
      if (regex.test(viaMatch[1])) {
        console.log('Extracted bank from via:', bank);
        return bank;
      }
    }
    // If not found, return the first word after 'via' as fallback
    const viaBank = viaMatch[1].trim().split(' ')[0];
    console.log('Extracted bank name after "via":', viaBank);
    return viaBank;
  }
  // 2. Check for e-wallets in the whole text (word boundary)
  for (const ew of ewallets) {
    const regex = new RegExp(`\\b${ew.toLowerCase()}\\b`, 'i');
    if (regex.test(cleanText)) {
      console.log('Extracted e-wallet from OCR:', ew);
      return ew;
    }
  }
  // 3. Check for banks in the whole text (word boundary)
  for (const bank of banks) {
    const regex = new RegExp(`\\b${bank.toLowerCase()}\\b`, 'i');
    if (regex.test(cleanText)) {
      console.log('Extracted bank name from OCR:', bank);
      return bank;
    }
  }
  return null;
};

// Preprocess image: upscale, grayscale, threshold
const preprocessImage = (file) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = function () {
      const scale = 2; // Upscale factor
      const canvas = document.createElement('canvas');
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      // Grayscale + threshold
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < imageData.data.length; i += 4) {
        const avg = (imageData.data[i] + imageData.data[i+1] + imageData.data[i+2]) / 3;
        // Simple threshold at 180 (tweakable)
        const val = avg > 180 ? 255 : 0;
        imageData.data[i] = imageData.data[i+1] = imageData.data[i+2] = val;
      }
      ctx.putImageData(imageData, 0, 0);
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/png');
    };
    img.src = URL.createObjectURL(file);
  });
};

// OCR Processing function to extract transaction reference, amount, and transfer date
const processImageWithOCR = async (file) => {
  if (!file) return;
  if (formData.value.payment_method !== 'bank_transfer') {
    return;
  }
  try {
    isProcessingOCR.value = true;
    console.log('Starting OCR processing for bank transfer data extraction...');
    // --- Preprocess the image before OCR ---
    const processedBlob = await preprocessImage(file);
    // Import Tesseract.js dynamically to avoid bundling issues
    const { createWorker } = await import('tesseract.js');
    const worker = await createWorker('eng');
    // Create image URL for processing
    const imageUrl = URL.createObjectURL(processedBlob);
    // Process the image with OCR
    const { data: { text } } = await worker.recognize(imageUrl);
    console.log('OCR extracted text:', text);
    ocrExtractedText.value = text;
    URL.revokeObjectURL(imageUrl);
    await worker.terminate();
    // Extract various data from the OCR text
    const transactionRef = extractTransactionReference(text);
    const amount = extractAmount(text);
    const transferDate = extractTransferDate(text);
    const bankName = extractBankName(text);
    let extractedCount = 0;
    let extractedFields = [];
    // Auto-fill transaction reference
    if (transactionRef) {
      console.log('Extracted transaction reference:', transactionRef);
      if (!formData.value.transaction_reference) {
        formData.value.transaction_reference = transactionRef;
        extractedFields.push(`Reference: ${transactionRef}`);
        extractedCount++;
      } else {
        const oldRef = formData.value.transaction_reference;
        formData.value.transaction_reference = transactionRef;
        extractedFields.push(`Reference updated: ${transactionRef}`);
        extractedCount++;
      }
    }
    // Auto-fill amount
    if (amount) {
      console.log('Extracted amount:', amount);
      if (!formData.value.amount || formData.value.amount === 0) {
        formData.value.amount = amount;
        extractedFields.push(`Amount: ₱${amount.toLocaleString()}`);
        extractedCount++;
      } else {
        const oldAmount = formData.value.amount;
        formData.value.amount = amount;
        extractedFields.push(`Amount updated: ₱${amount.toLocaleString()}`);
        extractedCount++;
      }
    }
    // Auto-fill transfer date
    if (transferDate) {
      console.log('Extracted transfer date:', transferDate);
      if (!formData.value.transfer_date) {
        formData.value.transfer_date = transferDate;
        extractedFields.push(`Date: ${new Date(transferDate).toLocaleDateString()}`);
        extractedCount++;
      } else {
        const oldDate = formData.value.transfer_date;
        formData.value.transfer_date = transferDate;
        extractedFields.push(`Date updated: ${new Date(transferDate).toLocaleDateString()}`);
        extractedCount++;
      }
    }
    // Auto-fill bank name
    if (bankName) {
      if (!formData.value.bank_name) {
        formData.value.bank_name = bankName;
        extractedFields.push(`Bank: ${bankName}`);
        extractedCount++;
        console.log('Auto-filled bank name:', bankName);
      }
    }
    // Show appropriate success/info message
    if (extractedCount > 0) {
      const message = `Auto-filled ${extractedCount} field${extractedCount > 1 ? 's' : ''}: ${extractedFields.join(', ')}`;
      toast.success(message, { duration: 5000 });
    } else {
      toast.info('Could not detect transaction details from image. Please enter manually.');
    }
  } catch (error) {
    console.error('OCR processing error:', error);
    toast.warning('Failed to extract transaction details from image. Please enter manually.');
  } finally {
    isProcessingOCR.value = false;
  }
};

// Function to extract transaction reference from OCR text
const extractTransactionReference = (text) => {
  // Clean up the text
  const cleanText = text.replace(/\s+/g, ' ').trim();
  console.log('Cleaned OCR text for reference extraction:', cleanText);

  // Handle cases where the reference number is broken into multiple lines (e.g., small screenshots)
  // Example: "Ref No. 5035 609" then "403855" on the next line should become 5035609403855
  const multilineRefRegex = /Ref(?:erence)?\.?\s*No\.?\s*[:\-]?\s*([0-9][0-9\s]{3,})/i;
  const multilineRefMatch = multilineRefRegex.exec(text);
  if (multilineRefMatch && multilineRefMatch[1]) {
    const part1 = multilineRefMatch[1].replace(/\D+/g, '');
    const rest = text.slice(multilineRefMatch.index + multilineRefMatch[0].length);
    const trailingDigitMatches = [...rest.matchAll(/\b\d{5,12}\b/g)];
    let part2 = '';
    if (trailingDigitMatches.length > 0) {
      const candidate = trailingDigitMatches[0];
      const candidateValue = candidate[0];
      // Exclude obvious date/time fragments
      const beforeCandidate = rest.slice(Math.max(0, candidate.index - 3), candidate.index);
      if (candidateValue.length >= 5 && candidateValue.length <= 12 && !beforeCandidate.includes(':') && candidateValue.length !== 4) {
        part2 = candidateValue;
      }
    }
    const combinedDigits = (part1 + part2).trim();
    if (combinedDigits.length >= 10 && combinedDigits.length <= 25) {
      console.log('Found multiline transaction reference:', combinedDigits);
      return combinedDigits;
    }
  }
  
  // High-priority patterns (most specific first) - these should match first
  const highPriorityPatterns = [
    // InstaPay/PESONet specific patterns (alphanumeric)
    /(?:Reference\s*no\.?\s*[:\-]?\s*)([A-Z0-9]{10,25})/i, // InstaPay: Reference no. IT0250624085857007
    /(?:Reference\s*No\.?\s*[:\-]?\s*)([A-Z0-9]{10,25})/i,
    /(?:InstaPay\s*Reference\.?\s*[:\-]?\s*)([A-Z0-9]{10,25})/i,
    /(?:PESONet\s*Reference\.?\s*[:\-]?\s*)([A-Z0-9]{10,25})/i,
    // BPI/Bank specific patterns
    /(?:Confirmation\s*No\.?\s*[:\-]?\s*)(\d{10,20})/i, // BPI: Confirmation No. 1750381383434
    /(?:Reference\s*Number\s*[:\-]?\s*)(\d{5,15})/i,    // Reference Number 00001139, 205144
    /(?:Reference\s*ID\s*[:\-]?\s*)([A-Za-z0-9]{8,20})/i, // Maya: Reference ID 6f5ee0aabbb3
    // GCash specific patterns (13-digit refs, with or without spaces)
    /(?:Ref\.?\s*No\.?\s*[:\-]?\s*)(\d{4}\s*\d{3}\s*\d{6})/i, // GCash with spaces: 1030 016 995414
    /(?:Ref\.?\s*No\.?\s*[:\-]?\s*)(\d{13})/i,               // GCash without spaces: 1030016995414
    /(?:Reference\.?\s*No\.?\s*[:\-]?\s*)(\d{4}\s*\d{3}\s*\d{6})/i,
    /(?:Reference\.?\s*No\.?\s*[:\-]?\s*)(\d{13})/i,
    /(?:GCash\s*Ref\.?\s*[:\-]?\s*)(\d{4}\s*\d{3}\s*\d{6})/i,
    /(?:GCash\s*Ref\.?\s*[:\-]?\s*)(\d{13})/i,
    // InstaPay with BN format (BDO)
    /(?:Reference\s*no\.?\s*[:\-]?\s*)(BN-\d{8}-\d{8})/i, // BN-20250616-58506651
    // Bank transfer reference patterns (numeric)
    /(?:Reference\s*No\.?\s*[:\-]?\s*)(\d{15,20})/i, // Bank example: Reference No. 202506200308229342
    /(?:Ref\.?\s*No\.?\s*[:\-]?\s*)(\d{15,20})/i,
    /(?:Transaction\s*Reference\.?\s*[:\-]?\s*)(\d{15,20})/i,
    /(?:Transfer\s*Reference\.?\s*[:\-]?\s*)(\d{15,20})/i,
    /(?:Confirmation\s*Number\.?\s*[:\-]?\s*)(\d{15,20})/i,
    // Maya patterns
    /(?:Transaction\s*ID\.?\s*[:\-]?\s*)(\d{12,15})/i,
    /(?:Maya\s*Ref\.?\s*[:\-]?\s*)(\d{12,15})/i,
    // PayPal patterns
    /(?:PayPal\s*Transaction\s*ID\.?\s*[:\-]?\s*)([A-Z0-9]{17})/i,
    // --- NEW: Tolerant pattern for OCR errors (e.g. "f" or "F" instead of "Ref") ---
    /(?:[Rr]?[Ee]?[Ff][\s\.:\-]*)?(\d{4}\s*\d{3}\s*\d{6})/i, // e.g. "f 2030 586 124771"
    /(?:[Rr]?[Ee]?[Ff][\s\.:\-]*)?(\d{13})/i, // e.g. "f 2030586124771"
  ];
  
  // Medium-priority patterns (less specific)
  const mediumPriorityPatterns = [
    // Generic reference patterns (alphanumeric)
    /(?:Reference\.?\s*[:\-]?\s*)([A-Z0-9]{10,25})/i,
    /(?:Transaction\.?\s*[:\-]?\s*)([A-Z0-9]{10,25})/i,
    /(?:Ref\.?\s*[:\-]?\s*)([A-Z0-9]{10,25})/i,
    
    // Generic reference patterns (numeric)
    /(?:Reference\.?\s*[:\-]?\s*)(\d{12,20})/i,
    /(?:Transaction\.?\s*[:\-]?\s*)(\d{12,20})/i,
    /(?:Ref\.?\s*[:\-]?\s*)(\d{12,20})/i,
    
    // Bank-specific patterns
    /(?:BPI\s*Reference\.?\s*[:\-]?\s*)([A-Z0-9]{8,25})/i,
    /(?:BDO\s*Reference\.?\s*[:\-]?\s*)([A-Z0-9]{8,25})/i,
    /(?:Metrobank\s*Reference\.?\s*[:\-]?\s*)([A-Z0-9]{8,25})/i,
    /(?:GoTyme\s*Reference\.?\s*[:\-]?\s*)([A-Z0-9]{8,25})/i,
  ];
  
  // Low-priority patterns (very generic - use as last resort)
  const lowPriorityPatterns = [
    // Long alphanumeric sequences (likely references)
    /\b([A-Z0-9]{15,25})\b/i, // Very long alphanumeric (like InstaPay)
    /\b([A-Z0-9]{12,14})\b/i, // Medium-long alphanumeric
    
    // Long numeric sequences (but exclude common account number lengths)
    /\b(\d{15,20})\b/, // Very long numbers (likely references)
    /\b(\d{13,14})\b/, // Medium-long numbers
    /\b(\d{12})\b/,    // 12-digit numbers
  ];
  
  // Patterns to EXCLUDE (account numbers, amounts, etc.)
  const excludePatterns = [
    /(?:Account\s*No\.?\s*[:\-]?\s*)(\d+)/i,        // Account numbers
    /(?:To\s*Account\s*No\.?\s*[:\-]?\s*)(\d+)/i,   // Recipient account
    /(?:From\s*Account\s*[:\-]?\s*)(\d+)/i,         // Source account
    /(?:Principal\s*Amount\s*[:\-]?\s*)(\d+)/i,     // Amounts
    /(?:Total\s*Amount\s*[:\-]?\s*)(\d+)/i,         // Total amounts
    /(?:Fee\s*[:\-]?\s*)(\d+)/i,                    // Fees
    /(?:Trace\s*No\.?\s*[:\-]?\s*)(\d+)/i,          // Trace numbers (usually short)
    /(?:Batch\s*No\.?\s*[:\-]?\s*)(\d+)/i,          // Batch numbers
  ];
  
  // Function to clean up extracted reference (remove spaces, normalize)
  const cleanReference = (reference) => {
    // Remove all spaces and normalize
    const cleaned = reference.replace(/\s+/g, '');
    console.log(`Cleaned reference: "${reference}" -> "${cleaned}"`);
    return cleaned;
  };

  // Function to check if a number should be excluded
  const shouldExclude = (number, context = '') => {
    // Exclude pure alpha strings (prevents picking up words like "transportation")
    if (/^[A-Za-z]+$/.test(number)) {
      console.log(`Excluding "${number}" - contains only letters`);
      return true;
    }
    // Check against exclude patterns
    for (const pattern of excludePatterns) {
      if (pattern.test(context)) {
        console.log(`Excluding "${number}" - matched exclude pattern: ${pattern}`);
        return true;
      }
    }
    
    // Exclude references that are too short (likely account numbers or trace IDs)
    if (number.length < 10) {
      console.log(`Excluding "${number}" - too short (${number.length} characters)`);
      return true;
    }
    
    // Exclude pure numeric sequences that are too short for modern references
    if (/^\d+$/.test(number) && number.length < 12) {
      console.log(`Excluding "${number}" - numeric sequence too short for reference (${number.length} digits)`);
      return true;
    }
    
    // Exclude numbers that look like amounts (contain decimals in context)
    if (context.includes('.') && /\d+\.\d{2}/.test(context)) {
      console.log(`Excluding "${number}" - appears to be an amount`);
      return true;
    }
    
    return false;
  };
  
  // Try high-priority patterns first
  console.log('Trying high-priority patterns...');
  for (const pattern of highPriorityPatterns) {
    const matches = [...cleanText.matchAll(new RegExp(pattern.source, pattern.flags + 'g'))];
    for (const match of matches) {
      if (match && match[1]) {
        const rawReference = match[1].trim();
        const cleanedReference = rawReference.replace(/\s+/g, '');
        const context = match[0]; // Full match for context
        if (cleanedReference.length >= 10 && cleanedReference.length <= 25) {
          console.log(`Found HIGH-PRIORITY transaction reference with pattern ${pattern}:`, cleanedReference);
          return cleanedReference;
        }
      }
    }
  }
  
  // Try medium-priority patterns
  console.log('Trying medium-priority patterns...');
  for (const pattern of mediumPriorityPatterns) {
    const matches = [...cleanText.matchAll(new RegExp(pattern.source, pattern.flags + 'g'))];
    for (const match of matches) {
      if (match && match[1]) {
        const rawReference = match[1].trim();
        const cleanedReference = cleanReference(rawReference);
        const context = match[0];
        
        if (!shouldExclude(cleanedReference, context) && cleanedReference.length >= 10 && cleanedReference.length <= 25) {
          console.log(`Found MEDIUM-PRIORITY transaction reference with pattern ${pattern}:`, cleanedReference);
          return cleanedReference;
        }
      }
    }
  }
  
  // Try low-priority patterns (last resort)
  console.log('Trying low-priority patterns...');
  for (const pattern of lowPriorityPatterns) {
    const matches = [...cleanText.matchAll(new RegExp(pattern.source, pattern.flags + 'g'))];
    for (const match of matches) {
      if (match && match[1]) {
        const rawReference = match[1].trim();
        const cleanedReference = cleanReference(rawReference);
        
        // For low-priority patterns, be extra careful about exclusions
        const contextStart = Math.max(0, match.index - 50);
        const contextEnd = Math.min(cleanText.length, match.index + match[0].length + 50);
        const context = cleanText.substring(contextStart, contextEnd);

        // Only accept if it contains at least one digit to avoid pure-word matches
        if (!/\d/.test(cleanedReference)) {
          console.log(`Skipping LOW-PRIORITY candidate without digits: "${cleanedReference}"`);
          continue;
        }
        
        if (!shouldExclude(cleanedReference, context) && cleanedReference.length >= 12 && cleanedReference.length <= 25) {
          console.log(`Found LOW-PRIORITY transaction reference with pattern ${pattern}:`, cleanedReference);
          console.log(`Context: "${context}"`);
          return cleanedReference;
        }
      }
    }
  }
  
  // --- Fallback: If no reference found, look for any 13-digit number in the text ---
  const fallbackMatch = cleanText.match(/\b\d{13}\b/);
  if (fallbackMatch) {
    console.log('Fallback: Found 13-digit number as reference:', fallbackMatch[0]);
    return fallbackMatch[0];
  }
  
  console.log('No valid transaction reference found');
  return null;
};

// Function to extract amount from OCR text
const extractAmount = (text) => {
  // Clean up the text
  const cleanText = text.replace(/\s+/g, ' ').trim();
  console.log('Cleaned OCR text for amount extraction:', cleanText);
  
  // Amount patterns for bank transfers (prioritized)
  const amountPatterns = [
    // Specific amount patterns with strong context (highest priority)
    /(?:Total\s*Amount\s*Sent\s*[:\-]?\s*)(?:PHP\s*|₱\s*|P\s*)?([0-9,]+\.?[0-9]*)/gi, // GCash: Total Amount Sent ₱1630.00
    /(?:Amount\s*[:\-]?\s*)([0-9,]+\.?[0-9]*)\s*(?:\r?\n|\s+)(?:Total\s*Amount\s*Sent)/gi, // GCash: Amount 1,630.00 (followed by Total Amount Sent)
    /(?:Transfer\s*Amount\s*[:\-]?\s*)?(?:PHP\s*|₱\s*|P\s*)([0-9,]+\.?[0-9]*)/gi,      // Transfer Amount PHP 920.00
    /(?:Transfer\s*amount\s*[:\-]?\s*)?(?:PHP\s*|₱\s*|P\s*)([0-9,]+\.?[0-9]*)/gi,      // Transfer amount PHP 1,086.00
    /(?:Net\s*Amount\s*[:\-]?\s*)([0-9,]+\.?[0-9]*)/gi,                                // Net Amount: 1,500.00
    /(?:Total\s*Amount\s*[:\-]?\s*)([0-9,]+\.?[0-9]*)/gi,                              // Total Amount: 2,150.00
    
    // PHP amounts with peso sign (medium priority)
    /(?:Amount\s*[:\-]?\s*)?(?:PHP\s*|₱\s*|P\s*)([0-9,]+\.?[0-9]*)/gi,
    /(?:Total\s*Amount\s*[:\-]?\s*)?(?:PHP\s*|₱\s*|P\s*)([0-9,]+\.?[0-9]*)/gi,
    /(?:Principal\s*Amount\s*[:\-]?\s*)?(?:PHP\s*|₱\s*|P\s*)([0-9,]+\.?[0-9]*)/gi,
    
    // Amount with labels (lower priority)
    /(?:Amount\s*[:\-]?\s*)([0-9,]+\.?[0-9]*)/gi,
    /(?:Total\s*Amount\s*[:\-]?\s*)([0-9,]+\.?[0-9]*)/gi,
    /(?:Principal\s*Amount\s*[:\-]?\s*)([0-9,]+\.?[0-9]*)/gi,
    
    // Generic currency patterns (as fallback)
    /(?:PHP|₱|P)\s*([0-9,]+\.?[0-9]*)/gi,
  ];
  
  // Patterns to exclude (account numbers, reference numbers, fees, etc.)
  const excludePatterns = [
    /(?:Account\s*No\.?\s*[:\-]?\s*)([0-9,]+)/gi,
    /(?:Reference\s*No\.?\s*[:\-]?\s*)([0-9,]+)/gi,
    /(?:Transaction\s*Reference\s*[:\-]?\s*)([0-9,]+)/gi,
    /(?:Confirmation\s*No\.?\s*[:\-]?\s*)([0-9,]+)/gi,
    /(?:Trace\s*ID\s*[:\-]?\s*)([0-9,]+)/gi,
    
    // Fee and charge patterns
    /(?:Fee\s*[:\-]?\s*)([0-9,]+\.?[0-9]*)/gi,
    /(?:Charge\s*[:\-]?\s*)([0-9,]+\.?[0-9]*)/gi,
    /(?:Service\s*Fee\s*[:\-]?\s*)([0-9,]+\.?[0-9]*)/gi,
    /(?:Transfer\s*Fee\s*[:\-]?\s*)([0-9,]+\.?[0-9]*)/gi,
    /(?:Transaction\s*fee\s*[:\-]?\s*)([0-9,]+\.?[0-9]*)/gi,
    
    // Balance and other non-transfer amounts
    /(?:Your\s*new\s*balance\s*[:\-]?\s*)([0-9,]+\.?[0-9]*)/gi,
    /(?:Current\s*balance\s*[:\-]?\s*)([0-9,]+\.?[0-9]*)/gi,
    /(?:Available\s*balance\s*[:\-]?\s*)([0-9,]+\.?[0-9]*)/gi,
    
    // Account numbers and phone numbers
    /(?:\+63\s*[0-9\s]+)/gi,
    /(?:09[0-9]{2}\s*[0-9]{3}\s*[0-9]{4})/gi,
  ];
  
  // Function to clean and parse amount
  const parseAmount = (amountStr) => {
    // Remove commas and convert to number
    const cleanAmount = amountStr.replace(/,/g, '');
    const amount = parseFloat(cleanAmount);
    
    // Validate amount (reasonable range for payments)
    if (isNaN(amount) || amount <= 0 || amount > 10000000) { // Max 10M
      return null;
    }
    
    // Exclude amounts that are too small (likely fees) or have too many decimal places
    if (amount < 1 || (cleanAmount.includes('.') && cleanAmount.split('.')[1].length > 2)) {
      return null;
    }
    
    return amount;
  };
  
  // Function to check if amount should be excluded based on context
  const shouldExcludeAmount = (amount, context) => {
    for (const pattern of excludePatterns) {
      if (pattern.test(context)) {
        console.log(`Excluding amount "${amount}" - matched exclude pattern: ${pattern}`);
        return true;
      }
    }
    
    // Exclude very long numbers (likely account numbers or references)
    const cleanAmount = amount.toString().replace(/[,.]/g, '');
    if (cleanAmount.length > 10) {
      console.log(`Excluding amount "${amount}" - too long (${cleanAmount.length} digits)`);
      return true;
    }
    
    return false;
  };
  
  // Try each pattern
  for (const pattern of amountPatterns) {
    const matches = [...cleanText.matchAll(pattern)];
    for (const match of matches) {
      if (match && match[1]) {
        const rawAmount = match[1].trim();
        const parsedAmount = parseAmount(rawAmount);
        const context = match[0]; // Full match for context
        
        if (parsedAmount && !shouldExcludeAmount(parsedAmount, context)) {
          console.log(`Found amount with pattern ${pattern}:`, parsedAmount);
          return parsedAmount;
        }
      }
    }
  }
  
  console.log('No valid amount found');
  return null;
};

// Function to extract transfer date from OCR text
const extractTransferDate = (text) => {
  // Clean up the text
  const cleanText = text.replace(/\s+/g, ' ').trim();
  console.log('Cleaned OCR text for date extraction:', cleanText);
  
  // Date patterns for bank transfers
  const datePatterns = [
    // Specific date/time patterns with strong context (highest priority)
    /(?:Jun\s*26,\s*2025\s*11:19\s*AM)/gi,                                             // GCash: Jun 26, 2025 11:19 AM
    /(?:Jun\s*27,\s*2025\s*12:17\s*PM)/gi,                                             // GCash: Jun 27, 2025 12:17 PM
    /(?:June\s*24,\s*2025\s*at\s*4:58\s*PM)/gi,                                        // InstaPay: June 24, 2025 at 4:58 PM
    /(?:Friday,\s*Jun\s*20\s*2025;\s*09:03:06\s*AM)/gi,                                // BPI: Friday, Jun 20 2025; 09:03:06 AM
    /(?:Jun\s*16,\s*2025\s*05:52\s*PM)/gi,                                             // BDO: Jun 16, 2025 05:52 PM
    /(?:12\s*Jun\s*2025,\s*11:48AM)/gi,                                                // Bank: 12 Jun 2025, 11:48AM
    
    // ISO datetime patterns
    /(?:Date\s*and\s*Time\s*[:\-]?\s*)([0-9]{4}[\/\-][0-9]{1,2}[\/\-][0-9]{1,2}\s*[0-9]{1,2}:[0-9]{2}:[0-9]{2})/gi, // 2025-06-19 13:08:14
    /(?:Created\s*on\s*[:\-]?\s*)([0-9]{1,2}[\/\-][0-9]{1,2}[\/\-][0-9]{4}\s*[0-9]{1,2}:[0-9]{2}\s*[AP]M)/gi,       // Jun 16, 2025 05:52 PM
    
    // Transfer/Transaction date patterns
    /(?:Transfer\s*Date\s*[:\-]?\s*)([0-9]{1,2}[\/\-][0-9]{1,2}[\/\-][0-9]{2,4})/gi,
    /(?:Transaction\s*Date\s*[:\-]?\s*)([0-9]{1,2}[\/\-][0-9]{1,2}[\/\-][0-9]{2,4})/gi,
    /(?:Date\s*[:\-]?\s*)([0-9]{1,2}[\/\-][0-9]{1,2}[\/\-][0-9]{2,4})/gi,
    
    // Date with month names (generic patterns)
    /((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s*[0-9]{1,2},?\s*[0-9]{4}\s*[0-9]{1,2}:[0-9]{2}\s*[AP]M)/gi, // Jun 26, 2025 11:19 AM
    /\b((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s*[0-9]{1,2},?\s*[0-9]{4})\s*[0-9]{1,2}:[0-9]{2}\s*[AP]M/gi, // Simpler: Jun 26, 2025 (followed by time)
    /(?:Transfer\s*Date\s*[:\-]?\s*)([0-9]{1,2}\s*(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s*[0-9]{2,4})/gi,
    /(?:Transaction\s*Date\s*[:\-]?\s*)([0-9]{1,2}\s*(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s*[0-9]{2,4})/gi,
    /(?:Date\s*[:\-]?\s*)([0-9]{1,2}\s*(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s*[0-9]{2,4})/gi,
    
    // SMS notification date pattern
    /([0-9]{2}[\/\-][0-9]{2}[\/\-][0-9]{4}\s*[0-9]{2}:[0-9]{2}\s*[AP]M)/gi,          // 06-25-2025 09:37 AM
    
    // ISO date formats
    /(?:Transfer\s*Date\s*[:\-]?\s*)([0-9]{4}[\/\-][0-9]{1,2}[\/\-][0-9]{1,2})/gi,
    /(?:Transaction\s*Date\s*[:\-]?\s*)([0-9]{4}[\/\-][0-9]{1,2}[\/\-][0-9]{1,2})/gi,
    
    // Generic date patterns (fallback)
    /\b([0-9]{1,2}[\/\-][0-9]{1,2}[\/\-][0-9]{4})\b/g,
    /\b([0-9]{4}[\/\-][0-9]{1,2}[\/\-][0-9]{1,2})\b/g,
    /\b([0-9]{1,2}\s*(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s*[0-9]{2,4})\b/gi,
    
    // Very simple fallback for month name dates
    /\b((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+[0-9]{1,2},?\s+[0-9]{4})\b/gi, // Jun 26, 2025
  ];
  
  // Function to parse and validate date
  const parseDate = (dateStr) => {
    try {
      // Clean the date string
      let cleanDate = dateStr.trim();
      
      // Handle different date formats
      let parsedDate;
      
      // Try to parse various formats
      const formats = [
        // Complex datetime formats (from sample images)
        /^([0-9]{4})[\/\-]([0-9]{1,2})[\/\-]([0-9]{1,2})\s*[0-9]{1,2}:[0-9]{2}:[0-9]{2}$/, // 2025-06-19 13:08:14
        /^([0-9]{2})[\/\-]([0-9]{2})[\/\-]([0-9]{4})\s*[0-9]{1,2}:[0-9]{2}\s*[AP]M$/i,     // 06-25-2025 09:37 AM
        
        // Month name with time formats
        /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s*([0-9]{1,2}),?\s*([0-9]{4})\s*[0-9]{1,2}:[0-9]{2}\s*[AP]M$/i, // Jun 26, 2025 11:19 AM
        /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s*([0-9]{1,2}),?\s*([0-9]{4})$/i, // Jun 26, 2025 (without time)
        /^(Jun|June)\s*([0-9]{1,2}),?\s*([0-9]{4})\s*(?:at\s*)?[0-9]{1,2}:[0-9]{2}\s*[AP]M$/i, // Jun 27, 2025 12:17 PM
        /^([0-9]{1,2})\s*(Jun|June)\s*([0-9]{4}),?\s*[0-9]{1,2}:[0-9]{2}[AP]M$/i,             // 12 Jun 2025, 11:48AM
        /^Friday,?\s*(Jun|June)\s*([0-9]{1,2})\s*([0-9]{4});\s*[0-9]{1,2}:[0-9]{2}:[0-9]{2}\s*[AP]M$/i, // Friday, Jun 20 2025; 09:03:06 AM
        
        // Standard date formats
        // MM/DD/YYYY or DD/MM/YYYY
        /^([0-9]{1,2})[\/\-]([0-9]{1,2})[\/\-]([0-9]{4})$/,
        // YYYY/MM/DD or YYYY-MM-DD
        /^([0-9]{4})[\/\-]([0-9]{1,2})[\/\-]([0-9]{1,2})$/,
        // DD MMM YYYY
        /^([0-9]{1,2})\s*(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s*([0-9]{2,4})$/i,
        // MMM DD, YYYY (simple fallback)
        /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+([0-9]{1,2}),?\s+([0-9]{4})$/i,
      ];
      
      for (const format of formats) {
        const match = cleanDate.match(format);
        if (match) {
          // Handle complex datetime formats
          if (format.source.includes('[0-9]{1,2}:[0-9]{2}:[0-9]{2}')) {
            // YYYY-MM-DD HH:mm:ss format
            parsedDate = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
            console.log(`Parsing YYYY-MM-DD HH:mm:ss: ${match[1]}-${match[2]}-${match[3]} -> Date object:`, parsedDate);
          } else if (format.source.includes('[AP]M')) {
            // MM-DD-YYYY HH:mm AM/PM format
            parsedDate = new Date(parseInt(match[3]), parseInt(match[1]) - 1, parseInt(match[2]));
            console.log(`Parsing MM-DD-YYYY HH:mm AM/PM: ${match[1]}/${match[2]}/${match[3]} -> Date object:`, parsedDate);
          } else if (format.source.includes('Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec')) {
            // Month name with time formats
            const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
                              'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
            if (match[1] && isNaN(match[1])) {
              // Format: Jun 27, 2025 or June 24, 2025
              const monthIndex = monthNames.indexOf(match[1].toLowerCase().substring(0, 3));
              parsedDate = new Date(parseInt(match[3]), monthIndex, parseInt(match[2]));
              console.log(`Parsing month-day-year: ${match[1]} ${match[2]}, ${match[3]} -> Month index: ${monthIndex}, Date object:`, parsedDate);
            } else {
              // Format: 12 Jun 2025 or Friday, Jun 20 2025
              const monthName = match[2] || match[1]; // Handle different capture groups
              const day = match[1] && !isNaN(match[1]) ? match[1] : match[2];
              const year = match[3];
              const monthIndex = monthNames.indexOf(monthName.toLowerCase().substring(0, 3));
              parsedDate = new Date(parseInt(year), monthIndex, parseInt(day));
              console.log(`Parsing day-month-year: ${day} ${monthName} ${year} -> Month index: ${monthIndex}, Date object:`, parsedDate);
            }
          } else if (format.source.includes('([0-9]{4})')) {
            // Standard YYYY format
            if (match[1].length === 4) {
              // YYYY/MM/DD
              parsedDate = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
              console.log(`Parsing YYYY/MM/DD: ${match[1]}/${match[2]}/${match[3]} -> Date object:`, parsedDate);
            } else {
              // MM/DD/YYYY or DD/MM/YYYY - assume MM/DD/YYYY for now
              parsedDate = new Date(parseInt(match[3]), parseInt(match[1]) - 1, parseInt(match[2]));
              console.log(`Parsing MM/DD/YYYY: ${match[1]}/${match[2]}/${match[3]} -> Date object:`, parsedDate);
            }
          } else if (format.source.includes('Jan|Feb')) {
            // Month name format
            const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
                              'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
            const monthIndex = monthNames.indexOf(match[2].toLowerCase().substring(0, 3));
            const year = match[3].length === 2 ? 2000 + parseInt(match[3]) : parseInt(match[3]);
            parsedDate = new Date(year, monthIndex, parseInt(match[1]));
            console.log(`Parsing DD MMM YYYY: ${match[1]} ${match[2]} ${match[3]} -> Month index: ${monthIndex}, Date object:`, parsedDate);
          }
          break;
        }
      }
      
      // If no specific format matched, try generic Date parsing
      if (!parsedDate) {
        parsedDate = new Date(cleanDate);
      }
      
      // Validate the date
      if (isNaN(parsedDate.getTime())) {
        return null;
      }
      
      // Check if date is reasonable (not too far in past/future)
      const now = new Date();
      const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      const oneMonthFromNow = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
      
      if (parsedDate < oneYearAgo || parsedDate > oneMonthFromNow) {
        console.log(`Date ${parsedDate.toISOString()} is outside reasonable range`);
        return null;
      }
      
      // Return in YYYY-MM-DD format for input field (timezone-safe)
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
      const day = String(parsedDate.getDate()).padStart(2, '0');
      const isoDate = `${year}-${month}-${day}`;
      console.log(`Converting Date object to ISO: ${parsedDate} -> ${isoDate}`);
      return isoDate;
      
    } catch (error) {
      console.log(`Error parsing date "${dateStr}":`, error);
      return null;
    }
  };
  
  // Try each pattern
  for (const pattern of datePatterns) {
    const matches = [...cleanText.matchAll(pattern)];
    console.log(`Trying date pattern ${pattern}:`, matches.length, 'matches');
    for (const match of matches) {
      if (match && match[1]) {
        const rawDate = match[1].trim();
        console.log(`Raw date extracted: "${rawDate}"`);
        const parsedDate = parseDate(rawDate);
        
        if (parsedDate) {
          console.log(`Found transfer date with pattern ${pattern}:`, parsedDate);
          return parsedDate;
        } else {
          console.log(`Failed to parse date: "${rawDate}"`);
        }
      }
    }
  }
  
  console.log('No valid transfer date found');
  return null;
};

const validateAndProcessImage = async (file) => {
  // Validate file type and size
  const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
  if (!validTypes.includes(file.type)) {
    toast.error('Please select a valid image file (JPG, PNG, or GIF)');
    return;
  }

  if (file.size > 10 * 1024 * 1024) { // 10MB limit
    toast.error('Image file size must be less than 10MB');
    return;
  }

  // Clear previous validation errors
  validationErrors.value.image_url = '';

  // Clear previous preview if exists
  if (imagePreview.value && imagePreview.value.startsWith('blob:')) {
    URL.revokeObjectURL(imagePreview.value);
  }

  // Set new file and preview
  formData.value.imageFile = file;
  imagePreview.value = URL.createObjectURL(file);
  formData.value.image_url = null; // Clear the old image URL when new image is uploaded
  
  // Show success message
  toast.success('Payment image loaded successfully!');
  
  // Process with OCR for transaction reference extraction
  if (formData.value.payment_method === 'bank_transfer') {
    toast.info('Processing image to extract transaction reference...');
    await processImageWithOCR(file);
  }
};

const removeImage = () => {
  // Clear the file input
  if (imageInput.value) {
    imageInput.value.value = '';
  }

  // Clear the preview if it's a blob URL
  if (imagePreview.value && imagePreview.value.startsWith('blob:')) {
    URL.revokeObjectURL(imagePreview.value);
  }

  // Reset all image-related values
  imagePreview.value = null;
  formData.value.imageFile = null;
  formData.value.image_url = null;
};

// Replace submitForm with handleSubmit
const handleSubmit = async (event) => {
  if (isSubmitting.value || submitDisabled.value) {
    console.log('[PaymentForm] Submission already in progress or cooldown, ignoring.');
    return;
  }
  if (!validateForm()) return;

  try {
    isSubmitting.value = true;
    submitDisabled.value = true;
    console.log('[PaymentForm] Processing payment submission...');

    // Create FormData for file upload
    const submitData = new FormData();
    submitData.append('order_id', formData.value.order_id);
    submitData.append('amount', formData.value.amount);
    submitData.append('payment_method', formData.value.payment_method);
    submitData.append('payment_type', formData.value.payment_type);
    submitData.append('payment_date', formData.value.date);
    submitData.append('status', formData.value.status);
    submitData.append('notes', formData.value.notes || '');
    if (formData.value.payment_method === 'bank_transfer') {
      submitData.append('bank_name', formData.value.bank_name);
      submitData.append('transaction_reference', formData.value.transaction_reference);
      submitData.append('transfer_date', formData.value.transfer_date);
    }
    if (formData.value.payment_method === 'check') {
      submitData.append('bank_name', formData.value.bank_name);
      submitData.append('cheque_number', formData.value.cheque_number);
      submitData.append('cheque_date', formData.value.cheque_date);
    }
    if (formData.value.payment_method === 'credit') {
      submitData.append('transaction_reference', formData.value.transaction_reference);
      submitData.append('card_type', formData.value.card_type);
    }
    if (formData.value.imageFile && formData.value.payment_method !== 'cash') {
      submitData.append('image', formData.value.imageFile);
      console.log('[PaymentForm] Including image file:', formData.value.imageFile.name);
    }
    // Emit the form data to parent
    console.log('[PaymentForm] Emitting submit event with data:', submitData);
    emit('submit', submitData);
    // Cooldown to prevent rapid double submissions
    setTimeout(() => {
      submitDisabled.value = false;
    }, 2000);
  } catch (error) {
    console.error('[PaymentForm] Error preparing payment data:', error);
    toast.error('Failed to prepare payment data. Please try again.');
    submitDisabled.value = false;
  } finally {
    isSubmitting.value = false;
  }
};

const loadPaymentTypes = async () => {
  try {
    const response = await api.get('/payments/types');
    if (response?.data) {
      console.log('Available payment types:', response.data);
      paymentTypes.value = response.data;
    }
  } catch (error) {
    console.error('Error loading payment types:', error);
    toast.error('Failed to load payment types');
  }
};

watch(() => formData.value.payment_type, (newValue, oldValue) => {
  console.log('Payment type changed:', { newValue, oldValue });
});

// Watch for amount changes to auto-select payment type
watch(() => formData.value.amount, (newAmount) => {
  if (!selectedOrder.value || !newAmount || newAmount <= 0) return;
  
  const totalAmount = Number(selectedOrder.value.total_amount) || 0;
  const totalPaid = Number(selectedOrder.value.total_paid) || 0;
  const remainingBalance = totalAmount - totalPaid;
  const paymentAmount = Number(newAmount);
  
  // Skip auto-selection if order is flagged for collection (already auto-selected)
  if (selectedOrder.value.for_collection) {
    return;
  }
  
  // Auto-select payment type based on amount
  if (paymentAmount >= remainingBalance && remainingBalance > 0) {
    // Payment covers remaining balance - mark as "Fully Paid"
    const fullyPaidType = paymentTypes.value.find(type => 
      type.name.toLowerCase().includes('fully paid') || 
      type.name.toLowerCase().includes('full payment')
    );
    if (fullyPaidType) {
      formData.value.payment_type = fullyPaidType.name;
      console.log(`Auto-selected payment type "${fullyPaidType.name}" - amount (₱${paymentAmount}) covers remaining balance (₱${remainingBalance})`);
    }
  } else if (totalPaid === 0 && paymentAmount < totalAmount) {
    // First payment and partial - mark as "Downpayment"
    const downpaymentType = paymentTypes.value.find(type => 
      type.name.toLowerCase().includes('downpayment') || 
      type.name.toLowerCase().includes('down payment')
    );
    if (downpaymentType) {
      formData.value.payment_type = downpaymentType.name;
      console.log(`Auto-selected payment type "${downpaymentType.name}" - first partial payment (₱${paymentAmount} of ₱${totalAmount})`);
    }
  } else if (totalPaid > 0 && paymentAmount < remainingBalance) {
    // Subsequent partial payment - mark as "Balance"
    const balanceType = paymentTypes.value.find(type => 
      type.name.toLowerCase().includes('balance') || 
      type.name.toLowerCase().includes('partial')
    );
    if (balanceType) {
      formData.value.payment_type = balanceType.name;
      console.log(`Auto-selected payment type "${balanceType.name}" - partial payment towards balance (₱${paymentAmount} of ₱${remainingBalance} remaining)`);
    }
  }
});

watch(() => formData.value.payment_method, async (newValue, oldValue) => {
  // Clear image validation error when payment method changes
  if (validationErrors.value.image_url) {
    validationErrors.value.image_url = '';
  }
  
  // If payment method changed to bank_transfer and we have an image, process OCR
  if (newValue === 'bank_transfer' && oldValue !== 'bank_transfer' && formData.value.imageFile) {
    console.log('Payment method changed to bank_transfer, processing existing image with OCR...');
    toast.info('Processing existing image to extract transaction reference...');
    await processImageWithOCR(formData.value.imageFile);
  }
});

watch(() => paymentTypes.value, (newValue) => {
  console.log('Payment types updated:', newValue);
  if (props.payment && props.payment.payment_type) {
    console.log('Checking if payment type exists:', props.payment.payment_type);
    const typeExists = newValue.some(type => type.name === props.payment.payment_type);
    console.log('Payment type exists in options:', typeExists);
  }
}, { immediate: true });

onMounted(async () => {
  await loadPaymentTypes();
  
  // Handle initial order if provided
  if (props.initialOrder) {
    console.log('Initial order provided:', props.initialOrder);
    
    try {
      // Fetch complete order details with payments
      const response = await api.get(`/orders/${props.initialOrder.id}`);
      if (response.data) {
        const orderData = response.data;
        
        // Fetch payments for this order
        const paymentsResponse = await api.get(`/orders/${props.initialOrder.id}/payments`);
        const payments = paymentsResponse?.data || [];
        
        // Calculate total paid from approved and completed payments
        const totalPaid = payments
          .filter(p => p.status === 'completed' || p.status === 'approved')
          .reduce((sum, p) => sum + Number(p.amount || 0), 0);
        
        // Calculate remaining balance
        const totalAmount = Number(orderData.total_amount) || 0;
        const remainingBalance = totalAmount - totalPaid;
        
        // Update the selected order with calculated values
        selectedOrder.value = {
          ...orderData,
          total_paid: totalPaid,
          remaining_balance: remainingBalance,
          payments: payments
        };
        
        // Set form data
        formData.value.order_id = orderData.id;
        orderSearch.value = orderData.order_number;
        
        // Auto-select payment type based on for_collection flag
        if (orderData.for_collection) {
          formData.value.payment_type = 'Collection';
          console.log('[PaymentForm] Auto-selected payment type "Collection" for order flagged for collection');
        }
        
        console.log('Automatically selected order:', {
          id: orderData.id,
          order_number: orderData.order_number,
          total_amount: totalAmount,
          total_paid: totalPaid,
          remaining_balance: remainingBalance,
          for_collection: orderData.for_collection
        });
      }
    } catch (error) {
      console.error('Error loading initial order details:', error);
      toast.error('Failed to load order details');
    }
  } else if (props.payment) {
    console.log('Payment data from props:', props.payment);
    
    // First get the order details to find the payment
    if (props.payment.order_id) {
      try {
        const response = await api.get(`/orders/${props.payment.order_id}`);
        if (response.data) {
          const orderData = response.data;
          
          // Fetch payments for this order
          const paymentsResponse = await api.get(`/orders/${props.payment.order_id}/payments`);
          const payments = paymentsResponse?.data || [];
          
          console.log('All payments for order:', payments);
          
          // Calculate total paid from approved and completed payments
          const totalPaid = payments
            .filter(p => p.status === 'completed' || p.status === 'approved')
            .reduce((sum, p) => sum + Number(p.amount || 0), 0);
          
          // Calculate remaining balance
          const totalAmount = Number(orderData.total_amount) || 0;
          const remainingBalance = totalAmount - totalPaid;
          
          console.log('Calculated values for order:', {
            total_amount: totalAmount,
            total_paid: totalPaid,
            remaining_balance: remainingBalance
          });
          
          // Update the selected order with calculated values
          selectedOrder.value = {
            ...orderData,
            total_paid: totalPaid,
            remaining_balance: remainingBalance,
            payments: payments
          };
          
          // Find the matching payment in the order's payments array
          const matchingPayment = payments.find(p => p.id === props.payment.id);
          console.log('Found matching payment:', matchingPayment);
          
          // Determine payment type based on order context
          // IMPORTANT: If order is flagged for collection, always use "Collection"
          let determinedPaymentType = '';
          if (matchingPayment) {
            // Check if order is flagged for collection first
            if (orderData.for_collection) {
              determinedPaymentType = 'Collection';
              console.log('Order flagged for collection - auto-setting payment type to "Collection"');
            } else if (totalPaid >= totalAmount) {
              determinedPaymentType = 'Fully Paid';
            } else if (totalPaid === 0) {
              determinedPaymentType = 'Collection';
            } else if (matchingPayment === payments[0]) {
              determinedPaymentType = 'Downpayment';
            } else {
              determinedPaymentType = 'Balance';
            }
            console.log('Determined payment type:', determinedPaymentType);
          }
          
          // Reset image preview if exists
          if (imagePreview.value && imagePreview.value.startsWith('blob:')) {
            URL.revokeObjectURL(imagePreview.value);
            imagePreview.value = null;
          }
          
          // Set form data with determined payment type and image
          formData.value = {
            date: new Date(props.payment.payment_date).toISOString().split('T')[0],
            order_id: props.payment.order_id,
            amount: Number(props.payment.amount) || 0,
            payment_method: props.payment.payment_method || 'cash',
            payment_type: determinedPaymentType,
            transaction_reference: props.payment.transaction_reference || '',
            notes: props.payment.notes || '',
            status: props.payment.status || 'completed',
            // Support legacy receiving_bank payloads while preferring bank_name
            bank_name: props.payment.bank_name || props.payment.receiving_bank || '',
            transfer_date: props.payment.transfer_date
              ? new Date(props.payment.transfer_date).toISOString().split('T')[0]
              : (props.payment.payment_date
                  ? new Date(props.payment.payment_date).toISOString().split('T')[0]
                  : ''),
            cheque_number: props.payment.cheque_number || '',
            cheque_date: props.payment.cheque_date ? new Date(props.payment.cheque_date).toISOString().split('T')[0] : '',
            card_type: props.payment.card_type || '',
            image_url: props.payment.image_url || null,
            imageFile: null
          };
          console.log('Form data after setting payment:', formData.value);
        }
      } catch (error) {
        console.error('Error loading order details:', error);
      }
    }

    orderSearch.value = props.payment.order_number || '';
  }
});

// Add this before onMounted
watch([() => paymentTypes.value, () => props.payment], ([newPaymentTypes, payment]) => {
  if (payment && payment.payment_type && newPaymentTypes.length > 0) {
    // Don't override if order is flagged for collection - it should always be "Collection"
    if (selectedOrder.value && selectedOrder.value.for_collection) {
      formData.value.payment_type = 'Collection';
      console.log('Order flagged for collection - keeping payment type as "Collection"');
      return;
    }
    
    // Find the matching payment type from the options
    const matchingType = newPaymentTypes.find(type => 
      type.name.toLowerCase() === payment.payment_type.toLowerCase()
    );
    if (matchingType) {
      formData.value.payment_type = matchingType.name;
      console.log('Set payment type from props:', matchingType.name);
    }
  }
}, { immediate: true });
</script> 